import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
	Building2,
	MapPin,
	Briefcase,
	Users,
	Save,
	Loader2,
	Edit3,
	X,
	Mail,
	Phone,
	Globe,
	MapPinned,
} from 'lucide-react'
import { Input, Select, Button } from '@components/common'
import './ProfileSettings.scss'

/**
 * Company Size Options
 */
const COMPANY_SIZE_OPTIONS = [
	{ value: '', label: 'Select company size' },
	{ value: '1-10', label: '1-10 employees' },
	{ value: '11-50', label: '11-50 employees' },
	{ value: '51-200', label: '51-200 employees' },
	{ value: '201-500', label: '201-500 employees' },
	{ value: '501-1000', label: '501-1000 employees' },
	{ value: '1000+', label: '1000+ employees' },
]

/**
 * Industry Options
 */
const INDUSTRY_OPTIONS = [
	{ value: '', label: 'Select industry' },
	{ value: 'Technology', label: 'Technology' },
	{ value: 'Healthcare', label: 'Healthcare' },
	{ value: 'Finance', label: 'Finance & Banking' },
	{ value: 'Manufacturing', label: 'Manufacturing' },
	{ value: 'Retail', label: 'Retail & E-commerce' },
	{ value: 'Education', label: 'Education' },
	{ value: 'Government', label: 'Government' },
	{ value: 'Non-profit', label: 'Non-profit' },
	{ value: 'Consulting', label: 'Consulting' },
	{ value: 'Legal', label: 'Legal' },
	{ value: 'Real Estate', label: 'Real Estate' },
	{ value: 'Transportation', label: 'Transportation & Logistics' },
	{ value: 'Energy', label: 'Energy & Utilities' },
	{ value: 'Media', label: 'Media & Entertainment' },
	{ value: 'Hospitality', label: 'Hospitality & Tourism' },
	{ value: 'Other', label: 'Other' },
]

/**
 * Get industry label from value
 */
const getIndustryLabel = (value) => {
	const option = INDUSTRY_OPTIONS.find((opt) => opt.value === value)
	return option?.label || value || 'Not specified'
}

/**
 * Get company size label from value
 */
const getCompanySizeLabel = (value) => {
	const option = COMPANY_SIZE_OPTIONS.find((opt) => opt.value === value)
	return option?.label || value || 'Not specified'
}

/**
 * Display Field Component - Shows a label and value in read-only mode
 */
const DisplayField = ({ label, value, icon: Icon }) => (
	<div className="profile-settings__display-field">
		<span className="profile-settings__display-label">
			{Icon && <Icon size={14} />}
			{label}
		</span>
		<span className="profile-settings__display-value">
			{value || <span className="profile-settings__empty">Not specified</span>}
		</span>
	</div>
)

DisplayField.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string,
	icon: PropTypes.elementType,
}

/**
 * Profile Settings Component
 *
 * View and edit company profile information
 * - Company info (name, email, phone, website)
 * - Address details
 * - Business details (industry, size)
 */
const ProfileSettings = ({
	company,
	onSave,
	saving = false,
}) => {
	// Edit mode state
	const [isEditing, setIsEditing] = useState(false)

	// Form state
	const [formData, setFormData] = useState({
		name: '',
		phone: '',
		website: '',
		address: '',
		city: '',
		state: '',
		country: '',
		postal_code: '',
		industry: '',
		company_size: '',
	})

	// Validation errors
	const [errors, setErrors] = useState({})

	// Track if form has changes
	const [hasChanges, setHasChanges] = useState(false)

	// Initialize form with company data
	useEffect(() => {
		if (company) {
			setFormData({
				name: company.name || '',
				phone: company.phone || '',
				website: company.website || '',
				address: company.address || '',
				city: company.city || '',
				state: company.state || '',
				country: company.country || '',
				postal_code: company.postal_code || '',
				industry: company.industry || '',
				company_size: company.company_size || '',
			})
			setHasChanges(false)
		}
	}, [company])

	/**
	 * Handle input change
	 */
	const handleChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}))
		setHasChanges(true)

		// Clear field error on change
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: null,
			}))
		}
	}

	/**
	 * Validate form
	 */
	const validateForm = () => {
		const newErrors = {}

		// Required fields
		if (!formData.name?.trim()) {
			newErrors.name = 'Company name is required'
		}

		// Optional but must be valid if provided
		if (formData.website?.trim() && !/^https?:\/\/.+/.test(formData.website)) {
			newErrors.website = 'Please enter a valid URL (starting with http:// or https://)'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	/**
	 * Handle form submit
	 */
	const handleSubmit = async (e) => {
		e.preventDefault()

		if (!validateForm()) return

		// Only send changed fields (exclude email as it's not editable)
		const changedData = {}
		Object.keys(formData).forEach((key) => {
			if (formData[key] !== (company?.[key] || '')) {
				changedData[key] = formData[key]
			}
		})

		if (Object.keys(changedData).length > 0) {
			await onSave(changedData)
			setHasChanges(false)
			setIsEditing(false)
		}
	}

	/**
	 * Handle cancel edit
	 */
	const handleCancel = () => {
		// Reset form to original company data
		if (company) {
			setFormData({
				name: company.name || '',
				phone: company.phone || '',
				website: company.website || '',
				address: company.address || '',
				city: company.city || '',
				state: company.state || '',
				country: company.country || '',
				postal_code: company.postal_code || '',
				industry: company.industry || '',
				company_size: company.company_size || '',
			})
		}
		setErrors({})
		setHasChanges(false)
		setIsEditing(false)
	}

	/**
	 * Format full address for display
	 */
	const getFullAddress = () => {
		const parts = [
			company?.address,
			company?.city,
			company?.state,
			company?.postal_code,
			company?.country,
		].filter(Boolean)
		return parts.length > 0 ? parts.join(', ') : null
	}

	// View Mode - Display company information
	if (!isEditing) {
		return (
			<div className="profile-settings">
				{/* Company Information - View Mode */}
				<section className="profile-settings__section">
					<div className="profile-settings__section-header">
						<h3 className="profile-settings__section-title">
							<Building2 size={18} />
							Company Information
						</h3>
						<Button
							variant="outline"
							size="small"
							onClick={() => setIsEditing(true)}
						>
							<Edit3 size={14} />
							Edit
						</Button>
					</div>

					<div className="profile-settings__view-grid">
						<DisplayField
							label="Company Name"
							value={company?.name}
							icon={Building2}
						/>
						<DisplayField
							label="Email"
							value={company?.email}
							icon={Mail}
						/>
						<DisplayField
							label="Phone"
							value={company?.phone}
							icon={Phone}
						/>
						<DisplayField
							label="Website"
							value={company?.website}
							icon={Globe}
						/>
					</div>
				</section>

				{/* Address Details - View Mode */}
				<section className="profile-settings__section">
					<div className="profile-settings__section-header">
						<h3 className="profile-settings__section-title">
							<MapPin size={18} />
							Address Details
						</h3>
					</div>

					<div className="profile-settings__view-grid">
						<DisplayField
							label="Full Address"
							value={getFullAddress()}
							icon={MapPinned}
						/>
					</div>

					{(company?.address || company?.city || company?.state || company?.country || company?.postal_code) && (
						<div className="profile-settings__view-grid profile-settings__view-grid--details">
							<DisplayField label="Street Address" value={company?.address} />
							<DisplayField label="City" value={company?.city} />
							<DisplayField label="State / Province" value={company?.state} />
							<DisplayField label="Country" value={company?.country} />
							<DisplayField label="Postal Code" value={company?.postal_code} />
						</div>
					)}
				</section>

				{/* Business Details - View Mode */}
				<section className="profile-settings__section">
					<div className="profile-settings__section-header">
						<h3 className="profile-settings__section-title">
							<Briefcase size={18} />
							Business Details
						</h3>
					</div>

					<div className="profile-settings__view-grid">
						<DisplayField
							label="Industry"
							value={getIndustryLabel(company?.industry)}
							icon={Briefcase}
						/>
						<DisplayField
							label="Company Size"
							value={getCompanySizeLabel(company?.company_size)}
							icon={Users}
						/>
					</div>
				</section>
			</div>
		)
	}

	// Edit Mode - Form for editing company information
	return (
		<div className="profile-settings profile-settings--editing">
			<form onSubmit={handleSubmit}>
				{/* Company Information - Edit Mode */}
				<section className="profile-settings__section">
					<div className="profile-settings__section-header">
						<h3 className="profile-settings__section-title">
							<Building2 size={18} />
							Company Information
						</h3>
						<div className="profile-settings__edit-badge">
							<Edit3 size={12} />
							Editing
						</div>
					</div>

					<div className="profile-settings__grid">
						<div className="profile-settings__field">
							<Input
								label="Company Name"
								value={formData.name}
								onChange={(e) => handleChange('name', e.target.value)}
								error={errors.name}
								required
								placeholder="Enter company name"
								disabled={saving}
							/>
						</div>

						<div className="profile-settings__field">
							<Input
								label="Email"
								type="email"
								value={company?.email || ''}
								disabled
								readOnly
								helperText="Email cannot be changed"
							/>
						</div>

						<div className="profile-settings__field">
							<Input
								label="Phone"
								type="tel"
								value={formData.phone}
								onChange={(e) => handleChange('phone', e.target.value)}
								placeholder="+1 (555) 123-4567"
								disabled={saving}
							/>
						</div>

						<div className="profile-settings__field">
							<Input
								label="Website"
								type="url"
								value={formData.website}
								onChange={(e) => handleChange('website', e.target.value)}
								error={errors.website}
								placeholder="https://www.example.com"
								disabled={saving}
							/>
						</div>
					</div>
				</section>

				{/* Address Details - Edit Mode */}
				<section className="profile-settings__section">
					<h3 className="profile-settings__section-title">
						<MapPin size={18} />
						Address Details
					</h3>

					<div className="profile-settings__grid">
						<div className="profile-settings__field profile-settings__field--full">
							<Input
								label="Street Address"
								value={formData.address}
								onChange={(e) => handleChange('address', e.target.value)}
								placeholder="123 Business Street, Suite 100"
								disabled={saving}
							/>
						</div>

						<div className="profile-settings__field">
							<Input
								label="City"
								value={formData.city}
								onChange={(e) => handleChange('city', e.target.value)}
								placeholder="New York"
								disabled={saving}
							/>
						</div>

						<div className="profile-settings__field">
							<Input
								label="State / Province"
								value={formData.state}
								onChange={(e) => handleChange('state', e.target.value)}
								placeholder="NY"
								disabled={saving}
							/>
						</div>

						<div className="profile-settings__field">
							<Input
								label="Country"
								value={formData.country}
								onChange={(e) => handleChange('country', e.target.value)}
								placeholder="United States"
								disabled={saving}
							/>
						</div>

						<div className="profile-settings__field">
							<Input
								label="Postal Code"
								value={formData.postal_code}
								onChange={(e) => handleChange('postal_code', e.target.value)}
								placeholder="10001"
								disabled={saving}
							/>
						</div>
					</div>
				</section>

				{/* Business Details - Edit Mode */}
				<section className="profile-settings__section">
					<h3 className="profile-settings__section-title">
						<Briefcase size={18} />
						Business Details
					</h3>

					<div className="profile-settings__grid">
						<div className="profile-settings__field">
							<label className="profile-settings__label">Industry</label>
							<Select
								value={formData.industry}
								onChange={(e) => handleChange('industry', e.target.value)}
								options={INDUSTRY_OPTIONS}
								disabled={saving}
							/>
						</div>

						<div className="profile-settings__field">
							<label className="profile-settings__label">
								<Users size={14} />
								Company Size
							</label>
							<Select
								value={formData.company_size}
								onChange={(e) => handleChange('company_size', e.target.value)}
								options={COMPANY_SIZE_OPTIONS}
								disabled={saving}
							/>
						</div>
					</div>
				</section>

				{/* Action Buttons */}
				<div className="profile-settings__actions">
					<Button
						type="button"
						variant="outline"
						onClick={handleCancel}
						disabled={saving}
					>
						<X size={16} />
						Cancel
					</Button>
					<Button
						type="submit"
						variant="primary"
						disabled={!hasChanges || saving}
					>
						{saving ? (
							<>
								<Loader2 size={16} className="animate-spin" />
								Saving...
							</>
						) : (
							<>
								<Save size={16} />
								Save Changes
							</>
						)}
					</Button>
				</div>
			</form>
		</div>
	)
}

ProfileSettings.propTypes = {
	company: PropTypes.object,
	onSave: PropTypes.func.isRequired,
	saving: PropTypes.bool,
}

export default ProfileSettings
